/**
 * <CommandPalette/> — global ⌘K / Ctrl+K search + quick-actions surface.
 *
 * Hybrid model per user's spec:
 *   • STATIC index — navigation targets + quick actions (always instant)
 *   • LIVE search  — orgs / users / staff / numbers / contacts via
 *                    GET /api/search?q=&types= (owner-only). Debounced 200ms.
 *
 * Global mount: ships with a `CommandPaletteProvider` at app root that
 * exposes an `open()` handle + registers the Ctrl+K/⌘K global shortcut and
 * "/" hotkey when not typing in an input. Recent searches persist in
 * localStorage (last 5).
 *
 * Any component can trigger the palette via:
 *
 *   const { open } = useCommandPalette();
 *   <button onClick={open}>Search</button>
 *
 * No new backend endpoints required beyond the tiny /api/search fan-out.
 * All navigation targets are the ones already declared in App.js so we
 * don't fork the router.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Command as CommandPrimitive } from "cmdk";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { DialogTitle } from "@/components/ui/dialog";
import * as VisuallyHiddenPrimitive from "@radix-ui/react-visually-hidden";
import {
  MagnifyingGlass, House, Users, PhoneCall, ChatText, CalendarBlank,
  Buildings, ShieldCheck, ClockCounterClockwise, CreditCard, HardDrives,
  Rocket, Gauge, Lightning, PlusCircle, EnvelopeSimple, PuzzlePiece,
  ArrowsClockwise, User, IdentificationCard, Wrench, Headphones,
  ChartBar, Hash, AddressBook, Waveform,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CommandPaletteContext = createContext({ open: () => {}, close: () => {} });
export const useCommandPalette = () => useContext(CommandPaletteContext);

const RECENT_KEY = "hbf.cmdk.recent";
const MAX_RECENT = 5;

// -----------------------------------------------------------------------------
// Route-based gate. The palette is scoped to what the CURRENT ROUTE exposes,
// NOT just the user's role. So a returning visitor with a valid session cookie
// who lands on `/` still only sees the public palette — internal entity names
// are never surfaced from a public URL.
//
// A route is "internal" iff its path starts with one of these prefixes:
// Command Palette is scoped to routes where power-user shortcut discovery
// matters. Historically this included `/app` (Agent/Supervisor workspace),
// but per launch-day UX decision the palette is restricted to Platform
// Owner and future Technical Engineer surfaces only. The Agent/Supervisor
// sidebar is small and always visible, so the palette added complexity
// without productivity payoff.
const INTERNAL_ROUTE_PREFIXES = ["/owner", "/internal", "/staff"];

function isInternalPath(pathname) {
  if (!pathname) return false;
  return INTERNAL_ROUTE_PREFIXES.some(p => pathname === p || pathname.startsWith(p + "/"));
}

// -----------------------------------------------------------------------------
// Static index — every nav destination + quick action lives here so the palette
// works even when the backend is offline. Kept flat (no nesting) on purpose so
// cmdk's fuzzy filter can do its job.
// -----------------------------------------------------------------------------
const NAV_ITEMS = {
  // Public nav — shown ONLY to unauthenticated visitors. Deliberately excludes
  // every internal route, entity, and category so nothing sensitive leaks
  // before login (Owner Console, Organizations, Billing, Providers, Staff,
  // Contacts, Numbers, Audit, Launch Wizard, etc. are ALL absent by design).
  public: [
    { id: "nav.pub.home",    label: "Home",               icon: House,          path: "/",               kw: "landing" },
    { id: "nav.pub.pricing", label: "Pricing",            icon: CreditCard,     path: "/#pricing",       kw: "plans cost" },
    { id: "nav.pub.login",   label: "Log in",             icon: User,           path: "/login",          kw: "sign in" },
    { id: "nav.pub.signup",  label: "Get started",        icon: PlusCircle,     path: "/signup",         kw: "sign up trial free" },
    { id: "nav.pub.contact", label: "Contact sales",      icon: EnvelopeSimple, path: "mailto:sales@hellobyflow.com?subject=Sales%20enquiry", kw: "enterprise talk" },
  ],
  owner: [
    { id: "nav.owner.home",       label: "Owner Console",             icon: House,                path: "/owner",             kw: "dashboard home" },
    { id: "nav.owner.orgs",       label: "Organizations",             icon: Buildings,            path: "/owner",             kw: "customers accounts", section: "orgs" },
    { id: "nav.owner.billing",    label: "Billing & Subscriptions",   icon: CreditCard,           path: "/owner/billing",     kw: "invoices revenue mrr" },
    { id: "nav.owner.pricing",    label: "Pricing Configuration",     icon: CreditCard,           path: "/owner/billing/config", kw: "stripe plans" },
    { id: "nav.owner.production", label: "Production Integrations",   icon: PuzzlePiece,          path: "/owner/production",  kw: "stripe twilio sentry resend r2 config providers" },
    { id: "nav.owner.launch",     label: "Launch Readiness Wizard",   icon: Rocket,               path: "/owner/launch",      kw: "checklist go-live" },
    { id: "nav.owner.readiness",  label: "Launch Readiness Report",   icon: Gauge,                path: "/owner/readiness",   kw: "score checks" },
    { id: "nav.owner.security",   label: "Security Center",           icon: ShieldCheck,          path: "/owner/security",    kw: "auth mfa events" },
    { id: "nav.owner.soc",        label: "SOC — Escalation Policies", icon: ShieldCheck,          path: "/owner/soc",         kw: "incidents oncall" },
    { id: "nav.owner.audit",      label: "Audit Center",              icon: ClockCounterClockwise, path: "/owner/audit",      kw: "logs history" },
    { id: "nav.owner.storage",    label: "Storage Analytics",         icon: HardDrives,           path: "/owner/storage",     kw: "r2 retention media" },
    { id: "nav.owner.rtcCaptures", label: "RTC Signaling Captures",   icon: Waveform,             path: "/owner/rtc-captures", kw: "webrtc group call failure timeline diagnostics" },
    { id: "nav.owner.identity",   label: "Identity Verification",     icon: IdentificationCard,   path: "/owner/identity",    kw: "kyc onfido stripe" },
    { id: "nav.owner.growth",     label: "Growth & Insights",         icon: ChartBar,             path: "/owner/growth",      kw: "funnel signups" },
    { id: "nav.owner.digest",     label: "Owner Digest (Email)",      icon: EnvelopeSimple,       path: "/owner/digest",      kw: "notifications" },
    { id: "nav.owner.staff",      label: "Internal Staff",            icon: Users,                path: "/owner/staff",       kw: "team support" },
    { id: "nav.owner.providers",  label: "Provider Orchestration",    icon: Wrench,               path: "/owner/providers",   kw: "twilio stripe voice sms" },
    { id: "nav.owner.settings",   label: "Owner Settings",            icon: Wrench,               path: "/owner/settings",    kw: "profile" },
  ],
  workspace: [
    { id: "nav.app.dashboard",    label: "Dashboard",          icon: House,        path: "/app",           kw: "home" },
    { id: "nav.app.dialer",       label: "Dialer",             icon: PhoneCall,    path: "/app/dialer",    kw: "call" },
    { id: "nav.app.contacts",     label: "Contacts",           icon: AddressBook,  path: "/app/contacts",  kw: "address book" },
    { id: "nav.app.calls",        label: "Call Logs",          icon: PhoneCall,    path: "/app/calls",     kw: "history" },
    { id: "nav.app.recordings",   label: "Recordings",         icon: Headphones,   path: "/app/recordings", kw: "audio playback" },
    { id: "nav.app.sms",          label: "Messages",           icon: ChatText,     path: "/app/sms",       kw: "sms text chat" },
    { id: "nav.app.meetings",     label: "Meetings",           icon: CalendarBlank, path: "/app/meetings", kw: "video conference" },
    { id: "nav.app.numbers",      label: "Phone Numbers",      icon: Hash,         path: "/app/numbers",   kw: "virtual e164" },
    { id: "nav.app.analytics",    label: "Analytics",          icon: ChartBar,     path: "/app/analytics", kw: "reports metrics" },
    { id: "nav.app.leaderboard",  label: "Leaderboard",        icon: ChartBar,     path: "/app/leaderboard", kw: "gamification" },
    { id: "nav.app.coaching",     label: "Coaching",           icon: Users,        path: "/app/coaching",  kw: "training" },
    { id: "nav.app.ivr",          label: "IVR Builder",        icon: Wrench,       path: "/app/ivr",       kw: "menu flow" },
    { id: "nav.app.billing",      label: "Billing (My Org)",   icon: CreditCard,   path: "/app/billing",   kw: "subscription plan seats" },
    { id: "nav.app.profile",      label: "My Profile",         icon: User,         path: "/app/profile",   kw: "account" },
  ],
};

const QUICK_ACTIONS = [
  { id: "qa.owner.newOrg",       label: "Create organization",       icon: PlusCircle,        path: "/owner",             kw: "new org add customer",  scope: "owner" },
  { id: "qa.owner.inviteStaff",  label: "Invite internal staff",     icon: PlusCircle,        path: "/owner/staff",       kw: "add employee",           scope: "owner" },
  { id: "qa.owner.openBilling",  label: "Open Billing & Pricing",    icon: CreditCard,        path: "/owner/billing",     kw: "revenue mrr",            scope: "owner" },
  { id: "qa.owner.openProd",     label: "Configure Integrations",    icon: PuzzlePiece,       path: "/owner/production",  kw: "stripe twilio sentry",   scope: "owner" },
  { id: "qa.owner.launch",       label: "Run Launch Wizard",         icon: Rocket,            path: "/owner/launch",      kw: "checklist go-live",      scope: "owner" },
  { id: "qa.owner.security",     label: "Open Security Center",      icon: ShieldCheck,       path: "/owner/security",    kw: "auth events",            scope: "owner" },
  { id: "qa.app.newCall",        label: "Place a call",              icon: PhoneCall,         path: "/app/dialer",        kw: "dial phone",             scope: "app" },
  { id: "qa.app.newContact",     label: "Add a contact",             icon: PlusCircle,        path: "/app/contacts",      kw: "new person",             scope: "app" },
  { id: "qa.app.newMessage",     label: "Send a message",            icon: ChatText,          path: "/app/sms",           kw: "sms text",               scope: "app" },
  { id: "qa.app.newMeeting",     label: "Schedule a meeting",        icon: CalendarBlank,     path: "/app/meetings",      kw: "video call",             scope: "app" },
];

// -----------------------------------------------------------------------------
// Type → icon map for backend search results
// -----------------------------------------------------------------------------
const RESULT_ICON = {
  org: Buildings,
  user: User,
  staff: Users,
  number: Hash,
  contact: AddressBook,
};


function CommandPaletteInner({ open, setOpen }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [liveResults, setLiveResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
  });
  const debounceRef = useRef(null);

  // -------------------------------------------------------------------------
  // Route- AND auth-gated visibility.
  //
  //   INTERNAL route (/app, /owner, /internal, /staff, /onboarding):
  //     → gate purely by role (anon shouldn't be there anyway — private routes
  //       redirect to /login before rendering; still, we defend in depth).
  //
  //   PUBLIC route (anything else — landing, /login, /signup, /pricing,
  //   /docs, marketing pages):
  //     → PUBLIC palette ONLY, regardless of whether the visitor happens to
  //       have a valid session cookie. This prevents any internal entity
  //       name from surfacing on a marketing URL that could be shared or
  //       screenshotted.
  // -------------------------------------------------------------------------
  const onInternalRoute = isInternalPath(location.pathname);
  const isAuthed = !!user && onInternalRoute;   // only "authed" for palette purposes on internal routes
  const isOwner  = isAuthed && (user?.role === "owner" || user?.role === "internal_staff");
  const canLiveSearch = isOwner;

  // Reset when opening
  useEffect(() => {
    if (open) { setQ(""); setLiveResults([]); }
  }, [open]);

  // Debounced live search (owner-only)
  useEffect(() => {
    if (!canLiveSearch) { setLiveResults([]); return; }
    if (!q || q.trim().length < 2) { setLiveResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const mod = await import("@/lib/api");
        const { data } = await mod.default.get("/search", {
          params: { q: q.trim(), types: "orgs,users,staff,numbers,contacts", limit: 6 },
        });
        setLiveResults(Array.isArray(data?.results) ? data.results : []);
      } catch {
        setLiveResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, canLiveSearch]);

  const pushRecent = useCallback((entry) => {
    setRecent(prev => {
      const dedup = prev.filter(r => r.id !== entry.id);
      const next = [entry, ...dedup].slice(0, MAX_RECENT);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* storage full or disabled */ }
      return next;
    });
  }, []);

  const run = useCallback((path, entry) => {
    setOpen(false);
    if (entry) pushRecent(entry);
    if (path?.startsWith("http")) window.location.href = path;
    else navigate(path);
  }, [navigate, setOpen, pushRecent]);

  // Filter nav/actions by auth + role — anonymous visitors get ONLY the
  // public nav; NO internal route names are ever surfaced pre-login.
  const visibleNav = useMemo(() => {
    if (!isAuthed) return NAV_ITEMS.public;
    if (isOwner)   return [...NAV_ITEMS.owner, ...NAV_ITEMS.workspace];
    return NAV_ITEMS.workspace;
  }, [isAuthed, isOwner]);

  const visibleActions = useMemo(() => {
    if (!isAuthed) return [];   // quick actions are ALL authenticated
    return QUICK_ACTIONS.filter(a => a.scope === "app" || (isOwner && a.scope === "owner"));
  }, [isAuthed, isOwner]);

  // Recent history is per-user; wipe it when the user logs out OR when we
  // navigate to a public route so a shared browser doesn't leak the previous
  // session's nav trail to a returning marketing-page visitor.
  useEffect(() => {
    if (!isAuthed) {
      try { localStorage.removeItem(RECENT_KEY); } catch { /* storage disabled */ }
      setRecent([]);
    }
  }, [isAuthed]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        {/* Subtle overlay — landing page stays partially visible (Raycast/Linear feel) */}
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-slate-950/30 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          aria-describedby="cmdk-description"
          className="fixed left-1/2 top-[18%] z-50 w-[92vw] max-w-[640px] -translate-x-1/2 overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-[0_20px_60px_-15px_rgba(0,0,0,0.25)] outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
        >
          <VisuallyHiddenPrimitive.Root>
            <DialogTitle>Command palette</DialogTitle>
            <div id="cmdk-description">Search organizations, users, staff, phone numbers, contacts, or navigate to any page. Use arrow keys to navigate, Enter to select, Escape to close.</div>
          </VisuallyHiddenPrimitive.Root>

          <CommandPrimitive className="flex flex-col outline-none">
            {/* Input row — flat, no border-bottom until list renders */}
            <div className="flex items-center gap-2 px-4 h-11 border-b border-slate-100">
              <MagnifyingGlass size={16} weight="regular" className="text-slate-400 shrink-0"/>
              <CommandPrimitive.Input
                placeholder={isAuthed ? "Search or type a command…" : "Search…"}
                value={q}
                onValueChange={setQ}
                data-testid="cmdk-input"
                className="flex-1 h-full bg-transparent text-[14px] text-slate-900 placeholder:text-slate-400 outline-none border-0"
              />
              {loading && <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">…</span>}
            </div>

            {/* List — dynamic height. min-h-0 so it collapses on small result sets;
                caps at ~55vh so ultra-long results scroll instead of pushing footer off-screen. */}
            <CommandPrimitive.List
              data-testid="cmdk-list"
              className="max-h-[min(55vh,480px)] overflow-y-auto overflow-x-hidden scroll-py-2 px-1.5 py-1"
            >
              <CommandPrimitive.Empty>
                <div className="px-4 py-6 text-center">
                  <div className="text-[13px] text-slate-600">
                    {loading ? "Searching…" : "No matches"}
                  </div>
                  {!loading && (
                    <div className="text-[11px] text-slate-400 mt-0.5">Try a shorter query or a different keyword</div>
                  )}
                </div>
              </CommandPrimitive.Empty>

              {/* Recent — only when no query */}
              {!q && recent.length > 0 && (
                <CommandPrimitive.Group heading="Recent" className={GROUP_CLS}>
                  {recent.map(entry => {
                    const Icon = (entry.iconKey && RESULT_ICON[entry.iconKey]) || ClockCounterClockwise;
                    return (
                      <PaletteItem
                        key={`recent-${entry.id}`}
                        value={`recent ${entry.label}`}
                        onSelect={() => run(entry.path, entry)}
                        testId={`cmdk-recent-${entry.id}`}
                        Icon={Icon}
                        label={entry.label}
                      />
                    );
                  })}
                </CommandPrimitive.Group>
              )}

              {/* Quick Actions — authenticated only */}
              {isAuthed && visibleActions.length > 0 && (
                <CommandPrimitive.Group heading="Quick actions" className={GROUP_CLS}>
                  {visibleActions.map(a => (
                    <PaletteItem
                      key={a.id}
                      value={`${a.label} ${a.kw || ""}`}
                      onSelect={() => run(a.path, { id: a.id, label: a.label, path: a.path })}
                      testId={`cmdk-action-${a.id}`}
                      Icon={a.icon}
                      iconClassName="text-[var(--accent)]"
                      label={a.label}
                    />
                  ))}
                </CommandPrimitive.Group>
              )}

              {/* Live backend results — owner only (RBAC-enforced server-side) */}
              {canLiveSearch && liveResults.length > 0 && (
                <CommandPrimitive.Group heading="Results" className={GROUP_CLS}>
                  {liveResults.map(r => {
                    const Icon = RESULT_ICON[r.type] || MagnifyingGlass;
                    return (
                      <PaletteItem
                        key={`${r.type}-${r.id}`}
                        value={`${r.type} ${r.label} ${r.sublabel || ""}`}
                        onSelect={() => run(r.url, { id: `${r.type}-${r.id}`, label: r.label, path: r.url, iconKey: r.type })}
                        testId={`cmdk-result-${r.type}-${r.id}`}
                        Icon={Icon}
                        label={r.label}
                        sublabel={r.sublabel}
                        tag={r.type}
                      />
                    );
                  })}
                </CommandPrimitive.Group>
              )}

              {/* Navigation — public for anon, workspace/owner for authed users */}
              <CommandPrimitive.Group heading={isAuthed ? "Navigate" : "Pages"} className={GROUP_CLS}>
                {visibleNav.map(n => (
                  <PaletteItem
                    key={n.id}
                    value={`${n.label} ${n.kw || ""}`}
                    onSelect={() => run(n.path, { id: n.id, label: n.label, path: n.path })}
                    testId={`cmdk-nav-${n.id}`}
                    Icon={n.icon}
                    label={n.label}
                  />
                ))}
              </CommandPrimitive.Group>
            </CommandPrimitive.List>

            {/* Footer — minimal, Raycast/Linear style */}
            <div className="border-t border-slate-100 px-3 h-9 flex items-center justify-between text-[11px] text-slate-400">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1"><Kbd>↑↓</Kbd><span>navigate</span></span>
                <span className="flex items-center gap-1"><Kbd>↵</Kbd><span>select</span></span>
                <span className="flex items-center gap-1"><Kbd>esc</Kbd><span>close</span></span>
              </div>
              {!isAuthed && <span data-testid="cmdk-anon-note" className="text-slate-400">Sign in for full search</span>}
            </div>
          </CommandPrimitive>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// ─── Palette primitives — kept private to this module ─────────────────────────

const GROUP_CLS =
  "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 " +
  "[&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-medium " +
  "[&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-widest " +
  "[&_[cmdk-group-heading]]:text-slate-400";

function PaletteItem({ value, onSelect, testId, Icon, iconClassName = "text-slate-500", label, sublabel, tag }) {
  return (
    <CommandPrimitive.Item
      value={value}
      onSelect={onSelect}
      data-testid={testId}
      className="group flex items-center gap-2.5 px-2 py-1.5 rounded-md cursor-pointer text-[13px] text-slate-800 outline-none data-[selected=true]:bg-slate-100 data-[selected=true]:text-slate-900 aria-selected:bg-slate-100"
    >
      <Icon size={15} weight="regular" className={`${iconClassName} shrink-0`} />
      <span className="flex-1 min-w-0 truncate leading-none py-0.5">
        {label}
        {sublabel && <span className="ml-2 text-[11px] text-slate-400">{sublabel}</span>}
      </span>
      {tag && (
        <span className="text-[9px] font-mono uppercase tracking-widest text-slate-400 shrink-0 group-data-[selected=true]:text-slate-500">
          {tag}
        </span>
      )}
    </CommandPrimitive.Item>
  );
}

function Kbd({ children }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded bg-slate-100 text-slate-500 text-[10px] font-mono leading-none">
      {children}
    </kbd>
  );
}


export function CommandPaletteProvider({ children }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  // Only intercept ⌘F / "/" when we're on an internal route AND authed.
  // Public marketing routes must preserve native browser Find behaviour even
  // if the visitor happens to have a valid session cookie.
  const shortcutsEnabled = !!user && isInternalPath(location.pathname);
  const openCb = useCallback(() => setOpen(true), []);
  const closeCb = useCallback(() => setOpen(false), []);

  useEffect(() => {
    // Shortcuts are ONLY active on internal routes AND when authed. This
    // means /app (Agent/Supervisor) does NOT respond to ⌘K / Ctrl+K / ⌘F
    // / "/" — the palette is fully unregistered for those routes, matching
    // the launch-day decision to restrict the palette to Owner + future
    // Technical Engineer surfaces (see INTERNAL_ROUTE_PREFIXES above).
    if (!shortcutsEnabled) return undefined;
    const onKey = (e) => {
      const target = e.target;
      const inField = target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" ||
                      target?.isContentEditable;
      // ⌘K / Ctrl+K — toggle open/close.
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen(prev => !prev);
        return;
      }
      // ⌘F / Ctrl+F — intercepted only on internal routes; native Find
      // stays intact on the marketing site (already excluded by the guard
      // above).
      if ((e.metaKey || e.ctrlKey) && (e.key === "f" || e.key === "F")) {
        e.preventDefault();
        setOpen(true);
        return;
      }
      // "/" power-user shortcut — not while typing in an input.
      if (e.key === "/" && !inField && !open) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, shortcutsEnabled]);

  return (
    <CommandPaletteContext.Provider value={{ open: openCb, close: closeCb }}>
      {children}
      <CommandPaletteInner open={open} setOpen={setOpen} />
    </CommandPaletteContext.Provider>
  );
}


/**
 * Trigger button — drop this into any nav bar. Renders a lightweight
 * "Search... ⌘K" button that opens the palette. Auth-aware label.
 */
export function CommandPaletteTrigger({ className = "", compact = false }) {
  const { open } = useCommandPalette();
  const { user } = useAuth();
  const location = useLocation();
  const onInternal = isInternalPath(location.pathname);
  const shortcut = (typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform)) ? "⌘K" : "Ctrl+K";
  const label = user && onInternal ? "Search…" : "Search pages";
  return (
    <button
      onClick={open}
      className={`flex items-center gap-2 border border-[var(--border)] rounded-md hover:border-slate-400 hover:bg-slate-50 transition-colors text-[12px] text-slate-500 ${compact ? "px-2 py-1" : "px-3 py-1.5"} ${className}`}
      data-testid="cmdk-trigger"
      aria-label={user && onInternal ? "Open search (Ctrl+K)" : "Open page navigation (Ctrl+K)"}
    >
      <MagnifyingGlass size={14} />
      {!compact && <span>{label}</span>}
      <kbd className="ml-1 px-1.5 py-0.5 text-[10px] font-mono bg-slate-100 rounded">{shortcut}</kbd>
    </button>
  );
}
